import root from 'window-or-global';

import {
  getSessionState,
  SessionProps,
  setSessionState,
} from 'components/Analytics/google-analytics-store';
import env from 'env/env';
import { getCookie } from 'utils/cookie';
import { getFeatureFlags } from 'utils/feature-flags';
import locator from 'utils/locator';

import { handleDataLayerEvent } from './contentsquare-ga-adapter';
import {
  convertGtmEventToGa4EventParams,
  GtmCheckoutClickConfirmEvent,
  GtmCheckoutReviewOrderEvent,
  GtmGenericEvent,
} from './convertGtmEventToGa4EventParams';

export function getGoogleAnalyticsURL(): string {
  const { checkout_debugGoogleAnalytics: debugGoogleAnalytics } = getFeatureFlags();
  const gaUrl = new URL(
    debugGoogleAnalytics
      ? 'https://wtr-digital-analytics.ew.r.appspot.com/data'
      : env.googleAnalytics.collectApiUrl,
  );
  gaUrl.searchParams.set('v', '2');
  gaUrl.searchParams.set('measurement_id', env.googleAnalytics.measurementId);
  return gaUrl.toString();
}

// common parameters for all events, as specified in GA4 implementation docs:
//   https://johnlewispartnership.atlassian.net/wiki/spaces/WD/pages/621544687/Convert+existing+Checkout+events+to+Measurement+Protocol#The-Solution---MVP
const commonParams = [
  'branch_id',
  // 'channel', -- in beacon event params, calculated from URL param isMob=Y
  'content_group',
  'customer_id',
  'instant_checkout_availability_reason',
  'instant_checkout_available',
  'instant_checkout_eligibility',
  'mywaitrose_member',
  'order_id',
  'page_location',
  'page_path',
  'page_title',
  'page_type',
  'partner_discount_status',
  'previous_path',
  'purchase_or_amend',
];

const defaultProps: SessionProps = {
  branch_id: '',
  content_group: '',
  customer_id: '',
  instant_checkout_availability_reason: '',
  instant_checkout_available: false,
  instant_checkout_eligibility: false,
  mywaitrose_member: false,
  order_id: '',
  page_location: '',
  page_path: '',
  page_title: '',
  page_type: '',
  partner_discount_status: 'in-active',
  previous_path: '',
  purchase_or_amend: '',
};

function getUpdatedSessionProperties(eventArgs: unknown[]): SessionProps {
  const currentProps: SessionProps = getSessionState().props || {};
  const updatedProps: SessionProps = {};
  eventArgs.forEach(eventArg => {
    const arg = eventArg as Record<
      string,
      | string
      | number
      | boolean
      | null
      | undefined
      | Record<string, string | number | boolean | null | undefined>
    >;
    if (arg.event === 'transaction_completed') {
      // Do not update session properties for transaction_completed event, as this event is fired after the purchase is completed
      // and it can interfere with the clearing of the session.
      return;
    }
    if (arg?.user) {
      const userProps = arg.user as Record<string, string | undefined>;
      if (userProps.branch_id) {
        updatedProps.branch_id = userProps.branch_id;
      }
      if (userProps.customerId || userProps.customer_id) {
        updatedProps.customer_id = userProps.customerId || userProps.customer_id;
      }
      if (userProps.mywaitrose_customer) {
        updatedProps.mywaitrose_member = userProps.mywaitrose_customer === 'Y';
      }
    }
    if (arg?.page) {
      const pageProps = arg.page as Record<string, string | undefined>;
      if (pageProps.location) {
        updatedProps.page_location = pageProps.location;
      }
      if (pageProps.path) {
        updatedProps.page_path = pageProps.path;
      }
      if (pageProps.title) {
        updatedProps.page_title = pageProps.title;
      }
      if (pageProps.type) {
        updatedProps.page_type = pageProps.type;
        updatedProps.content_group = pageProps.type;
      }
      if (pageProps.previous_path) {
        updatedProps.previous_path = pageProps.previous_path;
      }
    }
    if (arg.partner_discount_status && typeof arg.partner_discount_status === 'string') {
      updatedProps.partner_discount_status = arg.partner_discount_status || 'in-active';
    }
    if (arg.instant_checkout_eligibility !== undefined) {
      updatedProps.instant_checkout_eligibility = !!arg.instant_checkout_eligibility;
      updatedProps.instant_checkout_available = updatedProps.instant_checkout_eligibility;
    }
    if (arg.instant_checkout_available !== undefined) {
      updatedProps.instant_checkout_available = !!arg.instant_checkout_available;
      updatedProps.instant_checkout_eligibility = updatedProps.instant_checkout_available;
    }
    if (
      arg.instant_checkout_availability_reason &&
      typeof arg.instant_checkout_availability_reason === 'string'
    ) {
      updatedProps.instant_checkout_availability_reason = arg.instant_checkout_availability_reason;
    }
    if (arg.event === 'checkout_review_order') {
      const event = eventArg as GtmCheckoutReviewOrderEvent;
      if (event.ecommerce?.checkout?.actionField?.id) {
        updatedProps.order_id = event.ecommerce?.checkout?.actionField?.id;
      }
      if (event.ecommerce?.checkout?.actionField?.option) {
        updatedProps.purchase_or_amend = event.ecommerce?.checkout?.actionField?.option;
      }
      if (event.ecommerce?.checkout?.actionField?.service_type) {
        updatedProps.service_type = event.ecommerce?.checkout?.actionField?.service_type;
      }
    }
    if (arg.event === 'checkout_click_confirm') {
      const event = eventArg as GtmCheckoutClickConfirmEvent;
      if (event.ecommerce?.checkout?.actionField?.payment_type) {
        updatedProps.payment_type = event.ecommerce?.checkout?.actionField?.payment_type;
      }
      if (event.ecommerce?.checkout?.actionField?.option) {
        updatedProps.purchase_or_amend = event.ecommerce?.checkout?.actionField?.option;
      }
    }
  });

  const props: SessionProps = { ...currentProps, ...updatedProps };

  // We do not need to update the session if no props are updated.
  if (Object.keys(updatedProps).length > 0) {
    setSessionState({ props });
  }

  // Ensure customer_id is set to some value but do not store it in session
  const customerId = props.customer_id || getCookie('customerId') || '-1';

  return { ...defaultProps, ...props, user_id: customerId, customer_id: customerId };
}

export const sendGoogleAnalyticsEvent = (
  clientId: string | null,
  sessionId: string | null,
  eventArgs: unknown[],
) => {
  if (__SERVER__ || !clientId || !sessionId || !eventArgs?.length) {
    return;
  }

  // Store and apply any user properties
  const sessionProps = getUpdatedSessionProperties(eventArgs);

  const gaEvents = eventArgs.filter(
    (obj: unknown) => obj && typeof obj === 'object' && 'event' in obj && !!obj.event,
  );
  if (gaEvents.length === 0) {
    return;
  }

  const urlParams = new URLSearchParams(locator.search);
  const channel = urlParams.get('isMob') === 'Y' ? 'app' : 'web';
  const eventUrlParams = Object.fromEntries(urlParams.entries());
  const sessionParams = Object.fromEntries(
    Object.entries(sessionProps).filter(([key]) => commonParams.includes(key)),
  );

  const events = gaEvents.map(eventObj => ({
    params: {
      channel,
      ...eventUrlParams,
      ...sessionParams, // set session properties first, as sometimes event properties have overlapping keys.
      user_id: sessionProps.customer_id, // set user_id to customer_id for GA4, as per GA4 implementation docs
      ...convertGtmEventToGa4EventParams(eventObj as GtmGenericEvent, sessionProps),
      session_id: sessionId,
    },
  }));

  const body = JSON.stringify({
    client_id: clientId,
    events,
  });

  root.navigator.sendBeacon(getGoogleAnalyticsURL(), body);

  gaEvents.forEach(event => handleDataLayerEvent(event as GtmGenericEvent, sessionProps));
};
